import { analyticsTheme } from "../analyticsTheme";
import { secondsToMinutes } from "../../../projects/helpers/time/secondsToMinutes";

export const getSharedPlugins = () => ({
  legend: {
    position: "top" as const,
    align: "center" as const,
    labels: {
      usePointStyle: true,
      pointStyle: "rectRounded" as const,
      padding: 20,
      font: { size: 13 },
    },
  },
  tooltip: {
    backgroundColor: analyticsTheme.tooltipBg,
    titleColor: analyticsTheme.textTitle,
    bodyColor: analyticsTheme.textBody,
    borderColor: analyticsTheme.tooltipBorder,
    borderWidth: 1,
    padding: 12,
    cornerRadius: 8,
    boxPadding: 6,
  },
});

export const getGridAxis = () => ({
  grid: {
    display: true,
    color: analyticsTheme.gridLine,
    drawTicks: false,
  },
  border: { dash: [4, 4] },
  ticks: {
    color: analyticsTheme.textTick,
    font: { size: 12 },
    padding: 8,
  },
});

export const getPercentageAxis = () => ({
  ...getGridAxis(),
  ticks: {
    ...getGridAxis().ticks,
    callback: (v: string | number) => `${v}%`,
  },
  beginAtZero: true as const,
  max: 100,
});

export const getCountAxis = () => ({
  ...getGridAxis(),
  ticks: {
    ...getGridAxis().ticks,
    precision: 0,
  },
  beginAtZero: true as const,
});

// Time-proportional x-axis: spacing between points reflects elapsed time,
// not data-point index.
export const getTimeAxis = () => ({
  ...getGridAxis(),
  type: "time" as const,
  time: {
    // Never zoom below day granularity, so axis labels stay dates (not times)
    // even when all points fall within a single day.
    minUnit: "day" as const,
    tooltipFormat: "PP", // e.g. "Mar 5, 2026"
    displayFormats: {
      day: "MMM d",
      week: "MMM d",
      month: "MMM yyyy",
    },
  },
  ticks: {
    ...getGridAxis().ticks,
    maxRotation: 0,
    autoSkip: true,
  },
});

// Numeric x-axis in seconds, labelled as mm:ss. `max` (video length in
// seconds) pins the axis end to the video; otherwise it fits the data.
export const getTimestampAxis = (max?: number) => ({
  ...getGridAxis(),
  type: "linear" as const,
  beginAtZero: true as const,
  ...(max != null && max > 0 ? { max } : {}),
  ticks: {
    ...getGridAxis().ticks,
    maxRotation: 0,
    autoSkip: true,
    callback: (v: string | number) => secondsToMinutes(Number(v)),
  },
});

export const getCategoryAxis = () => ({
  grid: { display: false },
  ticks: {
    color: analyticsTheme.textBody,
    font: { size: 12 },
    padding: 8,
  },
});

export const withClippedTicks = <T extends { ticks?: object }>(axis: T) => ({
  ...axis,
  ticks: {
    ...axis.ticks,
    autoSkip: true,
    maxTicksLimit: 12,
    maxRotation: 0,
    minRotation: 0,
  },
});
